/**
 * geoConfig.ts
 * ─────────────────────────────────────────────────────────────
 * Central configuration for geo-blocking and regional restrictions.
 * To add or remove a country, edit BLOCKED_COUNTRY_CODES only.
 * All dropdowns, banners, and detection hooks read from this file.
 * ─────────────────────────────────────────────────────────────
 */

/**
 * ISO 3166-1 alpha-2 country codes that are blocked from onboarding.
 * Falls back to env var REACT_APP_BLOCKED_COUNTRIES (comma-separated) if set.
 * Example env override: REACT_APP_BLOCKED_COUNTRIES=AF,KP,IR
 */
const envBlocked = process.env.REACT_APP_BLOCKED_COUNTRIES;

export const BLOCKED_COUNTRY_CODES: string[] = envBlocked
  ? envBlocked.split(',').map((c) => c.trim().toUpperCase())
  : [
      'AF', // Afghanistan
      'KP', // North Korea (Democratic People's Republic of Korea)
    ];

/**
 * Human-readable names matching the codes above.
 * Used in banner messages.
 */
export const BLOCKED_COUNTRY_NAMES: Record<string, string> = {
  AF: 'Afghanistan',
  KP: 'North Korea',
  IR: 'Iran',
  CU: 'Cuba',
  SY: 'Syria',
  SD: 'Sudan',
  MM: 'Myanmar',
  LY: 'Libya',
  SO: 'Somalia',
  YE: 'Yemen',
};

/**
 * Returns the display name for a country code.
 * Falls back to the code itself if not found.
 */
export function getCountryDisplayName(code: string): string {
  try {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return regionNames.of(code.toUpperCase()) || BLOCKED_COUNTRY_NAMES[code.toUpperCase()] || code;
  } catch {
    return BLOCKED_COUNTRY_NAMES[code.toUpperCase()] ?? code;
  }
}

/**
 * Returns true if the given ISO country code is in the blocked list.
 */
export function isCountryBlocked(countryCode: string): boolean {
  return BLOCKED_COUNTRY_CODES.includes(countryCode.toUpperCase());
}

/**
 * Returns the list of blocked country display names for use in UI messages.
 */
export function getBlockedCountryDisplayNames(): string[] {
  return BLOCKED_COUNTRY_CODES.map(getCountryDisplayName);
}
